import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Users, TrendingUp, Settings, Smartphone, ArrowRight, CheckCircle, ShoppingBag, MapPin, Shirt, UtensilsCrossed, Building, Store } from 'lucide-react';
import mockData from '../data/mock';

const MerchantSection = () => {
  const { merchantBenefits } = mockData;

  const iconMap = {
    Users,
    TrendingUp,
    Settings,
    Smartphone,
    ShoppingBag,
    MapPin,
    Shirt,
    UtensilsCrossed,
    Building,
    Store
  };

  return (
    <section id="merchant" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="bg-slate-900/10 text-slate-900 border-slate-900/20 mb-4">
            Untuk Merchant
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {merchantBenefits.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {merchantBenefits.subtitle}
          </p>
        </div>

        {/* Merchant Images Showcase */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img 
                src={merchantBenefits.merchantImage}
                alt="Indonesian Souvenir Shop"
                className="w-full h-[350px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                  <h3 className="font-bold text-slate-900 mb-2">Merchant Oleh-oleh</h3>
                  <p className="text-sm text-slate-600">Kerajinan tangan dan souvenir khas Indonesia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img 
                src={merchantBenefits.localMerchantImage}
                alt="Local Indonesian Merchant"
                className="w-full h-[350px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4">
                  <h3 className="font-bold text-slate-900 mb-2">Pedagang Lokal</h3>
                  <p className="text-sm text-slate-600">Kuliner dan produk segar dari merchant lokal</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {merchantBenefits.benefits.map((benefit, index) => {
            const IconComponent = iconMap[benefit.icon];
            return (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Merchant Types */}
        <div className="mb-20">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12">
            Jenis Merchant Partner Kami
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {merchantBenefits.merchantTypes.map((type, index) => {
              const IconComponent = iconMap[type.icon] || Users;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-105">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 mb-2">{type.title}</h4>
                    <p className="text-slate-600 text-sm">{type.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Partner Example */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <Badge className="bg-green-100 text-green-700 border-green-200 mb-4">
              Success Story
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Contoh Merchant Partner Sukses
            </h3>
            <div className="bg-gradient-to-br from-slate-900/10 to-slate-900/5 rounded-2xl p-8 border border-slate-900/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2D9CDB] to-blue-100 rounded-full flex items-center justify-center mr-4 shadow-lg">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-slate-900">{merchantBenefits.partnerExample.name}</h4>
                  <p className="text-slate-600">Merchant Fashion Premium</p>
                </div>
              </div>
              <p className="text-slate-700 mb-6">{merchantBenefits.partnerExample.description}</p>
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-green-700 font-semibold">{merchantBenefits.partnerExample.impact}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-xl font-bold text-slate-900">Mengapa Bergabung dengan SalingUntung?</h4>
            <div className="space-y-4">
              {[
                "Sistem komisi transparan dan otomatis",
                "Akses ke jaringan driver pariwisata terpercaya", 
                "Dashboard analitik untuk monitor performa bisnis",
                "Dukungan marketing digital untuk semua jenis bisnis",
              ].map((point, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-slate-900 mr-3 flex-shrink-0" />
                  <span className="text-slate-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50/50 rounded-3xl p-8 md:p-12 text-center mb-12">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            Mulai Tingkatkan Omzet Hari Ini
          </h3>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Bergabunglah dengan ratusan merchant yang sudah merasakan peningkatan signifikan dalam penjualan mereka.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <div className="text-3xl font-bold text-slate-900 mb-2">700+</div>
              <div className="text-slate-600">Driver Aktif</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-slate-900 mb-2">500+</div>
              <div className="text-slate-600">Agent Tour & Travel</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-slate-900 mb-2">85%</div>
              <div className="text-slate-600">Peningkatan Rata-rata Omzet</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => window.open('https://merchant.salinguntung.com', '_blank')}
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
          >
            Daftarkan Bisnismu Sekarang
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
          {/* <p className="text-slate-500 mt-4 text-sm">Gratis dan tanpa komitmen bulanan</p> */}
        </div>
      </div>
    </section>
  );
};

export default MerchantSection;